import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import ConstructionIcon from "@mui/icons-material/Construction";
import React, { ErrorInfo, ReactNode } from "react";
import { darkTheme, defaultTheme } from "../contexts/colorContext";
import { T } from "../components/T";

export const ErrorFallback: React.FC = () => {
  const darkmode = localStorage.getItem("darkmode");
  const theme = darkmode === "true" ? darkTheme : defaultTheme;

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.default,
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: 5,
            borderRadius: 4,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            textAlign: "center",
          }}
        >
          <Stack spacing={3} alignItems="center">
            <ConstructionIcon
              sx={{
                fontSize: 72,
                color: theme.palette.warning.main,
              }}
            />

            <Typography variant="h4" fontWeight="bold">
              <T str="Something went wrong" />
            </Typography>

            <Typography variant="body1" sx={{ maxWidth: 400 }}>
              <T str="An unexpected error occurred while trying to load this part of the application. Please try reloading the page." />
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={handleReload}
              size="large"
              sx={{ textTransform: "none", px: 4 }}
            >
              <T str="Reload the page" />
            </Button>

            <Divider flexItem sx={{ my: 2 }} />

            <Typography variant="caption">
              <T str="If the problem continues, please try again later or contact the SjorCRAFT team." />
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught error:", error, info);
    // Logging service (e.g. Sentry) can be added here
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }

    return this.props.children;
  }
}
